import axios from 'axios';
import {WebFormModel} from "capitalist-shared/src/core/model";
import {RemoteRequestState} from '@/backoffice/components/pages/taxrules/taxrules';
import {Paginator} from '@/models/layout/Pagination';
import {MoneyAmount} from '@/models/finance/money';
import {CurrencyCode} from "@/models/core/currency";
import {httpBackofficeAxiosApi} from "@/shared/axios.backoffice.helper";

export interface GateDocumentInterface extends Record<string, any> {
  date: number;
  gateDocumentId: number;
  investDocumentId: number;
  description: string;
  status: string;
  table: string;
  descriptionHumanReadable: string;
  info: string;
  is: {
    canNotifyDecline: boolean;
  }
  amount: MoneyAmount | null
}

export class GateDocument implements GateDocumentInterface {
  date!: number;

  gateDocumentId!: number;

  investDocumentId!: number;

  description!: string;

  status!: string;

  table!: string;

  descriptionHumanReadable!: string;

  info!: string;

  is: {
    canNotifyDecline: boolean
  } = {
    canNotifyDecline: false,
  }

  amount!: MoneyAmount

  processingModel?: GateStuckDocumentOperateModel = undefined;

  constructor() {
    this.processingModel = new GateStuckDocumentOperateModel();
    this.processingModel.document = this;
  }

  get hasInfo(): boolean {
    return Boolean(this.info) && (this.info.length > 0);
  }

  get investDocumentUrl(): string {
    return `/backoffice/default/document/id/${this.investDocumentId}`;
  }

  get uid() {
    return this.gateDocumentId;
  }

  get isProcessed(): boolean {
    return this.processingModel?.isProcessed;
  }
}

export enum GateStuckDocumentOperationsEnum {
  Success = 'success',
  Inquiry = 'inquiry',
  Reprocess = 'reprocess',
  Decline = 'decline',
  Idle = 'idle'
}

export class GateStuckDocumentOperateModel extends WebFormModel {
  static http = httpBackofficeAxiosApi;

  document?: GateDocument = undefined;

  operating: RemoteRequestState = new RemoteRequestState();

  values: {
    id: number,
    operation: GateStuckDocumentOperationsEnum,
    reason: string
  } = {
    id: 0,
    operation: GateStuckDocumentOperationsEnum.Success,
    reason: '',
  }

  done = false;

  doneOperation?: GateStuckDocumentOperationsEnum = undefined;

  get isProcessed(): boolean {
    return this.done;
  }

  async doSuccess() {
    return this.doOperation(GateStuckDocumentOperationsEnum.Success);
  }

  async doDecline() {
    return this.doOperation(GateStuckDocumentOperationsEnum.Decline);
  }

  async doInquiry() {
    return this.doOperation(GateStuckDocumentOperationsEnum.Inquiry);
  }

  async doReprocess() {
    return this.doOperation(GateStuckDocumentOperationsEnum.Reprocess);
  }

  async doOperation(operation: GateStuckDocumentOperationsEnum, declinationComment = '') {
    if (this.isBusy) return Promise.reject('Busy');
    this.errors.clear();
    this.values.id = this.document.gateDocumentId;
    this.values.operation = operation;
    this.values.reason = declinationComment;
    this.operating.loadStart();
    return GateStuckDocumentOperateModel.http.post('/backoffice/backofficewebapi/gatestuck',
      {call: 'operateDocument', m: this.values})
      .then((result) => {
        this.operating.loadEnd();
        this.doneOperation = this.values.operation;
        this.done = true;
        return result.data.model;
      }).catch((err) => {
        this.operating.loadError();
        this.checkAxiosError(err);
        // if (err.response.status == 422) {
        //     this.errors.setErrors(err.response.data.model.errors);
        // } else {
        //     this.errors.set('http', 'Response Status ' + err.response.status);
        // }
        throw err;
      });
  }

  get isBusy(): boolean {
    return this.operating.isLoading;
  }

  get busyOperation(): GateStuckDocumentOperationsEnum {
    return this.isBusy ? this.values.operation : GateStuckDocumentOperationsEnum.Idle;
  }
}

export enum GateDocumentsTypeEnum {
  Stuck = 'stuck',
  Onexecution = 'onexecution'
}

export interface DocumentDetailsInterface extends Record<string, any> {
  id: number
  user: {
    id: number
    profileUrl: string | null
    username: string | null
  },
  state: string,
  comment: string
  createdAt: number
  channel: string
  verificationType: string
  children: number[]
  batch: any
  amount: number
  destAmount: number
  tax: number
  currency: CurrencyCode
  destCurrency: CurrencyCode
  outcome: boolean
  description: string
  correspondent: string
  raw: any
  senderAccount: any
  isSystemSenderAccount: boolean
  isSourceAccountSystem: boolean
  isDestAccountSystem: boolean
  sourceAccount: string | null
  destAccount: string | null
}

export class GateInternalDocumentDetailsModel extends WebFormModel {
  document: DocumentDetailsInterface | null = null

  values: {
    id: number
  } = {
    id: 0,
  }

  loadingStatus: RemoteRequestState = new RemoteRequestState();

  loadDocumentDetails(id: number) {
    this.values.id = id;
    this.loadingStatus.loadStart();

    return httpBackofficeAxiosApi.post('/backoffice/backofficewebapi/gatestuck',
      {
        call: 'documentDetails', m: this.values,
      })
      .then((result) => {
        this.document = result.data.model.document ? ({...result.data.model.document}) : null;
        this.loadingStatus.loadEnd();
        return result.data.model.document;
      }).catch((err) => {
        this.checkAxiosError(err);
        this.loadingStatus.loadError();
        throw err;
      });
  }
}

export class GateStuckListModel extends WebFormModel {
  values: {
    p: number
    docType: GateDocumentsTypeEnum
    pageSize: number
  } = {
    p: 1,
    docType: GateDocumentsTypeEnum.Stuck,
    pageSize: 100,
  }

  documents: GateDocument[] = [];

  p: Paginator = new Paginator();

  loading: RemoteRequestState = new RemoteRequestState();

  http = httpBackofficeAxiosApi;

  async countStuckDocuments(pagesize = 100, docType: GateDocumentsTypeEnum = GateDocumentsTypeEnum.Stuck) {
    return this.http.post('/backoffice/backofficewebapi/gatestuck/countDocuments',
      {
        call: 'countDocuments',
        m: {
          pageSize: pagesize,
          docType,
        },
      })
      .then((result) => (new Paginator(result.data.model.pagination)));
  }

  async loadStuckDocumentsPage(page = 1, pagesize = 100, docType: GateDocumentsTypeEnum = GateDocumentsTypeEnum.Stuck) {
    return this.http.post('/backoffice/backofficewebapi/gatestuck/loadDocuments',
      {
        call: 'loadDocuments',
        m: {
          page,
          pageSize: pagesize,
          docType,
        },
      })
      .then((result) => {
        const documents = [...result.data.model.documents].map((obj) => Object.assign(new GateDocument(), obj));
        const p = (new Paginator(result.data.model.paginator));

        return {documents, p};
      });
  }

  loadStuckDocuments(docType: GateDocumentsTypeEnum = GateDocumentsTypeEnum.Stuck) {
    this.loading.loadStart();

    this.values.docType = docType;
    return this.countStuckDocuments(this.values.pageSize, docType).then((paginator: Paginator) => {
      if (paginator.total == 0) return [];

      return axios.all(paginator.totalRange().map((p) => this.loadStuckDocumentsPage(p, paginator.pagesize, docType))).then((responseArr) => {
        const documents = responseArr.map((resp) => resp.documents).flat().sort((a, b) => b.gateDocumentId - a.gateDocumentId);
        this.documents = documents;

        this.loading.loadEnd();

        return documents;
      }).catch((error) => {
        this.loading.loadError();
        if (error.response.status == 422) {
          this.errors.setErrors(error.response.data.model.errors);
        }
        throw error;
      });
    });

    // return axios.post('/backoffice/backofficewebapi/gatestuck',
    //     {call: 'loadDocuments'})
    //     .then((result) => {
    //
    //         this.documents = [...result.data.model.documents].map((obj) => Object.assign(new GateStuckDocument(), obj));
    //         this.p = (new Paginator(result.data.model.paginator));
    //         this.loading.loadEnd();
    //         return result.data.model;
    //     })
    //     .catch((error) => {
    //         this.loading.loadError();
    //         if (error.response.status == 422) {
    //             this.errors.setErrors(error.response.data.model.errors);
    //         }
    //         throw error;
    //     });
  }
}

export class RawResponsesModel extends WebFormModel {
  values: { id: number } = {id: 0}

  loading: RemoteRequestState = new RemoteRequestState();

  rawResponses = null;

  loadHistory() {
    this.loading.loadStart();

    return httpBackofficeAxiosApi.post('/backoffice/backofficewebapi/rawresponse', {
      call: 'Rawresponse',
      m: this.values,
    }).then((result) => {
      this.loading.loadEnd();
      this.rawResponses = {...result.data.model.rawResponses};
      return result.data.model;
    }).catch((err) => {
      this.loading.loadError();
      this.checkAxiosError(err);
      throw err;
    });
  }
}

export class GateSettingsModel extends WebFormModel {
  values: { id: number } = {id: 0}

  loading: RemoteRequestState = new RemoteRequestState();

  settingsValues = [];
  settingsValuesCascade = [];

  loadSettings() {
    this.loading.loadStart();

    return httpBackofficeAxiosApi.get('/backofficeapi/gate/settings?call=loadSettings',)
      .then((result) => {
        this.loading.loadEnd();

        result.data.settings.forEach(el => {
          if (el.name.match(/_CASCADE/)) {
            this.settingsValuesCascade.push(el);
          } else {
            this.settingsValues.push(el);
          }
        });
        return [this.settingsValues, this.settingsValuesCascade];
      }).catch((err) => {
        this.loading.loadError();
        this.checkAxiosError(err);
        throw err;
      });
  }

  saveSettings(name, value) {
    this.loading.loadStart();

    return httpBackofficeAxiosApi.post('/backofficeapi/gate/settings', {
      call: 'saveSettings',
      m: {
        name,
        value,
      },
    }).then((result) => {
      this.loading.loadEnd();
      return true;
    }).catch((err) => {
      this.loading.loadError();
      this.checkAxiosError(err);
      throw err;
    });
  }
}
